<template>
  <div class="rm-screen">
    <div class="rm-chip-row gm-chips" role="group" :aria-label="$t('view')">
      <button
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--active': type === 'orders' }"
        :aria-pressed="String(type === 'orders')"
        @click="type = 'orders'"
      >
        {{ $t("orders") }}
      </button>
      <button
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--active': type === 'meals' }"
        :aria-pressed="String(type === 'meals')"
        @click="type = 'meals'"
      >
        {{ $t("report_group.meals_view") }}
      </button>
      <button
        type="button"
        class="rm-chip"
        :class="{ 'rm-chip--active': type === 'foods' }"
        :aria-pressed="String(type === 'foods')"
        @click="type = 'foods'"
      >
        {{ $t("meals_main") }}
      </button>
    </div>

    <div v-if="loading && !reportItem.length" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <template v-if="filters.search">{{ $t("report_mobile.no_results_for", { search: filters.search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-truck-delivery-outline</v-icon>
        {{ $t("no_taken_group_meals_data") }}
      </template>
    </div>

    <template v-else>
      <div class="rm-stats">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("ordered") }}</span>
          <span class="rm-stat__value rm-num">{{ summary.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("taken") }}</span>
          <span class="rm-stat__value rm-num">{{ summary.taken }}</span>
        </div>
        <div class="rm-stat rm-stat--accent">
          <span class="rm-stat__label">{{ $t("difference") }}</span>
          <span class="rm-stat__value rm-num">{{ summary.difference }}</span>
        </div>
      </div>

      <ul class="rm-list rm-card">
        <li v-for="row in rows" :key="row.id" class="rm-mrow rm-mrow--action">
          <button type="button" class="rm-mrow__btn" @click="openCompany(row)">
            <span class="rm-mrow__text">
              <span class="rm-mrow__main">
                <span class="rm-mrow__name">{{ row.name }}</span>
                <span class="rm-mrow__value rm-num" :class="{ 'is-complete': row.complete }">
                  <v-icon v-if="row.complete" size="16" color="#15803d">mdi-check-circle</v-icon>
                  {{ row.ordered }}<span class="rm-mrow__of"> / {{ row.taken }}</span>
                </span>
              </span>
              <div
                class="rm-progress gm-taken__bar"
                :class="{ 'rm-progress--complete': row.complete }"
                role="progressbar"
                :aria-valuenow="row.taken"
                aria-valuemin="0"
                :aria-valuemax="row.ordered"
              >
                <div class="rm-progress__fill" :style="{ width: `${row.percent}%` }"></div>
              </div>
              <span v-if="row.shifts.length" class="rm-mrow__shifts">
                <span v-for="shift in row.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
                  <report-shift-tag :shift="shift.id"></report-shift-tag>
                  {{ shift.ordered }} / {{ shift.taken }}
                </span>
              </span>
            </span>
            <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
          </button>
        </li>
      </ul>
    </template>

    <ui-sheet v-model="actionSheet" :ready="Boolean(selectedRow)" :closable="false" :label="selectedRow ? selectedRow.name : ''">
      <template #title>{{ selectedRow.name }}</template>
      <template #default>
        <ul class="rm-list rm-actions">
          <li>
            <button type="button" class="rm-actions__row" @click="openEmployees">
              <v-icon size="22" color="#1c1917">mdi-account-multiple-outline</v-icon>
              {{ $t("company_employees_orders") }}
            </button>
          </li>
          <li>
            <button type="button" class="rm-actions__row" @click="openMonthly">
              <v-icon size="22" color="#1c1917">mdi-calendar-month-outline</v-icon>
              {{ $t("tooltip_monthly_report") }}
            </button>
          </li>
        </ul>
      </template>
    </ui-sheet>

    <report-group-meals-taken-company-monthly-orders
      v-model="dialogFirm"
      :firm-report-item="firmReportItem"
    ></report-group-meals-taken-company-monthly-orders>
  </div>
</template>

<script>
import ReportGroupMealsTakenCompanyMonthlyOrders from "@/components/report/group-meals/dialogs/ReportGroupMealsTakenCompanyMonthlyOrders.vue";
import ReportTabGroupMealsTabTaken from "@/components/report/group-meals/ReportTabGroupMealsTabTaken.vue";
import { SHIFTS } from "@/constants/shifts";
import hasBarShifts from "../../../shared/hasBarShifts";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasGroupTakenRows from "../../../shared/group/hasGroupTakenRows";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import "../reportMobileMealCards.scss";

/**
 * Mobilni "Naručeno / Preuzeto" (jela iz grupa): po firmi naručeno / preuzeto sa trakom napretka i po smeni.
 * "Narudžbe / Jela" su čipovi na vrhu, smene i period u zaglavlju. Klik na firmu nudi "Radnici" (Korisnici te firme)
 * i mesečni izveštaj firme (dijalog kao do sada). Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "GroupMobileTaken",
  extends: ReportTabGroupMealsTabTaken,
  mixins: [hasBarShifts, hasShiftSubtitle, hasGroupTakenRows],
  components: { ReportGroupMealsTakenCompanyMonthlyOrders, ReportShiftTag },
  data: () => ({
    actionSheet: false,
    selectedRow: null,
  }),
  computed: {
    rows() {
      return [...this.matchedRows]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((row) => ({
          ...row,
          shifts: SHIFTS.filter((shift) => this.selectedShifts.includes(shift.id) && row[shift.key].ordered > 0).map((shift) => ({
            id: shift.id,
            ordered: row[shift.key].ordered,
            taken: row[shift.key].taken,
          })),
        }));
    },
    summary() {
      return this.summarize(this.rows);
    },
  },
  methods: {
    openCompany(row) {
      this.selectedRow = row;
      this.actionSheet = true;
    },
    openEmployees() {
      this.actionSheet = false;
      this.selectCompanyForUsers(this.selectedRow.source);
      this.$emit("open-screen", "groupUsers");
    },
    openMonthly() {
      this.actionSheet = false;
      this.openDialogReportFirm(this.selectedRow.source);
    },
  },
};
</script>

<style lang="scss">
.rm-theme .gm-taken__bar {
  margin-top: 8px;
  height: 6px;
}
</style>
